import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/ui/Icon';
import { renderMarkdown } from '../../utils/markdown';
import { formatPostDate, postSummary, readingMinutes } from '../../utils/posts';
import './News.css';

// Building blocks shared by the homepage "Latest news" strip, /news, the
// /news/:slug article page and the dashboard editor's preview — so the preview
// an admin sees is the page visitors get.

function CoverImage({ post, className, eager = false }) {
  if (!post.cover_image_url) {
    // No cover uploaded: a branded placeholder keeps the card grid even.
    return (
      <div className={`${className} news-cover-placeholder`} aria-hidden="true">
        <Icon name="article" />
      </div>
    );
  }
  return (
    <img
      className={className}
      src={post.cover_image_url}
      alt={post.cover_image_alt || ''}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
    />
  );
}

function PostMeta({ post, showReadTime = false }) {
  const date = formatPostDate(post.published_at);
  return (
    <p className="news-meta">
      {date && <time dateTime={post.published_at}>{date}</time>}
      {post.author && <span>{post.author}</span>}
      {showReadTime && post.body && <span>{readingMinutes(post.body)} min read</span>}
    </p>
  );
}

export function NewsCard({ post, headingLevel = 'h3' }) {
  const Heading = headingLevel;
  return (
    <article className="news-card">
      <Link to={`/news/${post.slug}`} className="news-card-link">
        <div className="news-card-media">
          <CoverImage post={post} className="news-card-image" />
        </div>
        <div className="news-card-body">
          <PostMeta post={post} />
          <Heading className="news-card-title">{post.title}</Heading>
          <p className="news-card-excerpt">{postSummary(post, 160)}</p>
          <span className="news-card-more" aria-hidden="true">
            Read more <Icon name="arrow_forward" />
          </span>
        </div>
      </Link>
    </article>
  );
}

export function NewsGrid({ posts, headingLevel }) {
  return (
    <div className="news-grid">
      {posts.map((post) => (
        <NewsCard key={post.id || post.slug} post={post} headingLevel={headingLevel} />
      ))}
    </div>
  );
}

/** The full article: header, cover, body. Used by NewsArticle and the editor preview. */
export function ArticleView({ post }) {
  return (
    <article className="news-article">
      <header className="news-article-header">
        <Link to="/news" className="news-article-back">
          <Icon name="arrow_back" aria-hidden="true" /> All news
        </Link>
        <h1 className="news-article-title">{post.title || 'Untitled post'}</h1>
        {post.excerpt && <p className="news-article-dek">{post.excerpt}</p>}
        <PostMeta post={post} showReadTime />
      </header>
      {post.cover_image_url && (
        <figure className="news-article-cover">
          <CoverImage post={post} className="news-article-cover-image" eager />
        </figure>
      )}
      <div className="news-prose">{renderMarkdown(post.body)}</div>
    </article>
  );
}
